import { useEffect, useState } from "react";
import {
  Building2,
  Loader2,
  Rocket,
  User,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatCurrency } from "@/api/client";
import {
  findCertificateProduct,
  loadCatalogProducts,
} from "@/lib/catalog";
import { useBuyNow } from "@/hooks/useBuyNow";
import { cn } from "@/lib/utils";
import type { Product } from "@/types/api";

/**
 * Botão "Compra Flash" — atalho pro checkout.
 *
 * Modos:
 * - `product` passado: click → useBuyNow(product) → CNH check → /checkout
 * - sem `product`: click → abre modal com PF/PJ/MEI → pick → useBuyNow(default)
 *
 * Defaults do modal usam `findCertificateProduct(..., "A1")` que respeita
 * `metadata.defaultSelection` do catálogo (A1 online 1 ano na prática).
 */

type Kind = "ECPF" | "ECNPJ" | "EMEI";

const KIND_META: Record<
  Kind,
  { label: string; tagline: string; icon: typeof User; accent: string }
> = {
  ECPF: {
    label: "e-CPF",
    tagline: "Pessoa física · IR, Gov.br, assinaturas",
    icon: User,
    accent: "text-primary",
  },
  ECNPJ: {
    label: "e-CNPJ",
    tagline: "Empresa · NF-e, eSocial, SPED",
    icon: Building2,
    accent: "text-secondary",
  },
  EMEI: {
    label: "e-MEI",
    tagline: "MEI · NF-e com CNPJ individual",
    icon: Rocket,
    accent: "text-accent",
  },
};

interface Props {
  /** Produto específico — se passado, click vai direto pro checkout. */
  product?: Product;
  /** Tamanho do botão. */
  size?: "sm" | "default" | "lg";
  /** Classe adicional. */
  className?: string;
  /** Texto do botão. Default "Compra Flash". */
  label?: string;
  /** Largura total do container. */
  fullWidth?: boolean;
}

export function FlashBuyButton({
  product,
  size = "default",
  className,
  label = "Compra Flash",
  fullWidth = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const buyNow = useBuyNow();

  const hasSpecificProduct = !!product;

  useEffect(() => {
    if (!open || catalog.length > 0) return;
    setLoading(true);
    void loadCatalogProducts()
      .then((data) => setCatalog(data))
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [open, catalog.length]);

  const picks: Array<{ kind: Kind; product: Product | undefined }> = (
    ["ECPF", "ECNPJ", "EMEI"] as Kind[]
  ).map((kind) => ({
    kind,
    product: catalog.length
      ? findCertificateProduct(catalog, kind, "A1")
      : undefined,
  }));

  const handleClick = () => {
    if (hasSpecificProduct && product) {
      buyNow(product);
    } else {
      setOpen(true);
    }
  };

  const handleBuy = (p: Product) => {
    setOpen(false);
    buyNow(p);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        size={size}
        className={cn(
          "bg-success hover:bg-success/90 font-bold gap-1.5",
          fullWidth && "w-full",
          className
        )}
      >
        <Zap className={size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} />
        {label}
      </Button>

      {!hasSpecificProduct && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-success/10">
                <Zap className="h-5 w-5 text-success" />
              </div>
              <DialogTitle className="text-xl">Compra Flash</DialogTitle>
              <DialogDescription>
                Escolha o tipo e vamos direto pro checkout. A gente já
                pré-seleciona o <strong className="text-foreground">A1 online de 1 ano</strong> —
                você pode trocar depois se quiser.
              </DialogDescription>
            </DialogHeader>

            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-2 py-2">
                {picks.map(({ kind, product: p }) => {
                  const meta = KIND_META[kind];
                  const Icon = meta.icon;
                  return (
                    <button
                      key={kind}
                      type="button"
                      disabled={!p}
                      onClick={() => p && handleBuy(p)}
                      className="w-full flex items-center gap-4 rounded-2xl border-2 border-border/80 p-4 text-left transition-all hover:border-success hover:bg-success/5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted ${meta.accent}`}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-foreground text-base leading-tight">
                          {meta.label}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                          {meta.tagline}
                        </p>
                      </div>
                      {p && (
                        <div className="text-right shrink-0">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            A partir de
                          </p>
                          <p className="text-base font-bold text-foreground">
                            {formatCurrency(p.priceCents)}
                          </p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <p className="text-[11px] text-center text-muted-foreground pt-2">
              Depois do click só perguntamos se você tem CNH pós-2017 (validação online). Nada mais.
            </p>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
